import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UploadCloud, Trash2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HFIX_CRITERIA = [
  { key: "faisabilite", label: "Faisabilité" },
  { key: "acceptabilite", label: "Acceptabilité" },
  { key: "couts_vs_avantages", label: "Coûts vs avantages" },
  { key: "efficacite", label: "Efficacité" },
  { key: "durabilite", label: "Durabilité" },
];

export default function ImportAnalysis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNational = user?.role === "Administrateur national";

  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState({ filename: "", file_base64: "" });
  const [facts, setFacts] = useState([]);
  const [factors, setFactors] = useState([]);
  const [hfixActions, setHfixActions] = useState([]);
  const [form, setForm] = useState({
    site: "", date_evenement: "", heure_evenement: "", type_evenement: "",
    personne_concernee: "", categorie_personne: "", consequence: "",
    description_initiale: "", localisation: "", activite: "", entreprise: "", equipe_analyse: "",
  });

  useEffect(() => {
    axios.get(`${API}/sites`).then((res) => setSites(res.data));
    axios.get(`${API}/parametres`).then((res) => setCategories(res.data.filter((p) => p.CATEGORIE === "CATEGORIE_PERSONNE")));
  }, []);

  useEffect(() => {
    if (!isNational && user?.site) setForm((f) => ({ ...f, site: user.site }));
  }, [isNational, user]);

  const siteName = useMemo(() => sites.find((s) => s.ID_SITE === form.site)?.NOM_SITE, [sites, form.site]);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const extract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await axios.post(`${API}/hfacs/import/extract`, body, { headers: { "Content-Type": "multipart/form-data" } });
      const { extracted, filename, file_base64 } = res.data;
      setDraft({ filename, file_base64 });
      setForm((f) => ({ ...f, ...extracted.metadata }));
      setFacts((extracted.facts || []).map((x) => ({ ...x })));
      setFactors((extracted.factors || []).map((x) => ({ ...x })));
      setHfixActions((extracted.hfix_actions || []).map((x) => ({ ...x })));
      setStep("review");
      toast.success(`Extraction IA terminée : ${extracted.facts?.length || 0} faits, ${extracted.factors?.length || 0} facteurs, ${extracted.hfix_actions?.length || 0} actions HFIX.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'extraction IA.");
    } finally {
      setExtracting(false);
    }
  };

  const confirmImport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/hfacs/import/confirm`, {
        ...form,
        facts: facts.map(({ date, heure, description_factuelle }) => ({ date, heure, description_factuelle })),
        factors: factors.map(({ id_facteur, justification }) => ({ id_facteur, justification })),
        hfix_actions: hfixActions.map(({ description_action, faisabilite, acceptabilite, couts_vs_avantages, efficacite, durabilite }) => ({
          description_action, faisabilite, acceptabilite, couts_vs_avantages, efficacite, durabilite,
        })),
        filename: draft.filename,
        file_base64: draft.file_base64,
      });
      toast.success(`Analyse ${res.data.ID_HFACS} importée avec succès.`);
      navigate(`/hfacs/${res.data.ID_HFACS}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'import.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <h1 className="font-heading font-black uppercase tracking-tight text-3xl mb-2">Importer une ancienne analyse</h1>
        <p className="text-sm text-muted-foreground mb-8">
          Uploadez un fichier Excel/CSV d'une étude HFACS déjà réalisée : l'IA en extrait automatiquement la chronologie,
          les facteurs HFACS et le plan HFIX. Vous pourrez tout vérifier et corriger avant l'import final.
        </p>

        {step === "upload" && (
          <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="import-upload-step">
            <Label>Fichier de l'étude (.xlsx, .xlsm ou .csv)</Label>
            <label className="flex flex-col items-center gap-2 border border-dashed border-border rounded-md p-10 cursor-pointer hover:border-primary/50 transition-colors">
              <UploadCloud className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{file ? file.name : "Cliquez pour sélectionner un fichier"}</span>
              <input
                type="file"
                accept=".xlsx,.xlsm,.csv"
                data-testid="import-file-input"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <Button onClick={extract} disabled={!file || extracting} data-testid="import-extract-button" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {extracting ? "Extraction IA en cours…" : "Extraire avec l'IA"}
            </Button>
          </div>
        )}

        {step === "review" && (
          <form onSubmit={confirmImport} className="space-y-6" data-testid="import-review-step">
            <div className="bg-card border border-border rounded-md p-6 space-y-5">
              <h2 className="font-heading uppercase text-sm font-semibold text-muted-foreground">Informations générales</h2>
              <div>
                <Label>Site</Label>
                {isNational ? (
                  <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
                    <SelectTrigger data-testid="import-site-select"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.ID_SITE} value={s.ID_SITE}>{s.NOM_SITE} ({s.CODE_SITE})</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <p className="font-mono text-sm text-muted-foreground py-2">{siteName || user?.site}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date événement</Label><Input type="date" required data-testid="import-date" value={form.date_evenement} onChange={update("date_evenement")} /></div>
                <div><Label>Heure événement</Label><Input type="time" data-testid="import-heure" value={form.heure_evenement} onChange={update("heure_evenement")} /></div>
              </div>
              <div><Label>Type d'événement</Label><Input required data-testid="import-type" value={form.type_evenement} onChange={update("type_evenement")} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Personne concernée</Label><Input data-testid="import-personne" value={form.personne_concernee} onChange={update("personne_concernee")} /></div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.categorie_personne} onValueChange={(v) => setForm({ ...form, categorie_personne: v })}>
                    <SelectTrigger data-testid="import-categorie-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.VALEUR} value={c.VALEUR}>{c.VALEUR}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description initiale</Label><Textarea required rows={3} data-testid="import-description" value={form.description_initiale} onChange={update("description_initiale")} /></div>
              <div><Label>Conséquence</Label><Textarea rows={2} data-testid="import-consequence" value={form.consequence} onChange={update("consequence")} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Localisation</Label><Input data-testid="import-localisation" value={form.localisation} onChange={update("localisation")} /></div>
                <div><Label>Activité</Label><Input data-testid="import-activite" value={form.activite} onChange={update("activite")} /></div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading uppercase text-sm font-semibold text-muted-foreground">Chronologie extraite ({facts.length})</h2>
                <Button type="button" size="sm" variant="outline" data-testid="import-add-fact" onClick={() => setFacts([...facts, { date: "", heure: "", description_factuelle: "" }])} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </Button>
              </div>
              <div className="space-y-3" data-testid="import-facts-list">
                {facts.map((f, i) => (
                  <div key={i} className="flex gap-2 items-start" data-testid={`import-fact-row-${i}`}>
                    <Input type="date" className="w-32 shrink-0" value={f.date || ""} onChange={(e) => setFacts(facts.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                    <Input type="time" className="w-24 shrink-0" value={f.heure || ""} onChange={(e) => setFacts(facts.map((x, j) => (j === i ? { ...x, heure: e.target.value } : x)))} />
                    <Textarea rows={1} className="flex-1" value={f.description_factuelle} onChange={(e) => setFacts(facts.map((x, j) => (j === i ? { ...x, description_factuelle: e.target.value } : x)))} />
                    <Button type="button" size="icon" variant="ghost" data-testid={`import-remove-fact-${i}`} onClick={() => setFacts(facts.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                {facts.length === 0 && <p className="text-xs text-muted-foreground">Aucun fait extrait.</p>}
              </div>
            </div>

            <div className="bg-card border border-border rounded-md p-6 space-y-4">
              <h2 className="font-heading uppercase text-sm font-semibold text-muted-foreground">Facteurs HFACS retenus ({factors.length})</h2>
              <div className="space-y-3" data-testid="import-factors-list">
                {factors.map((f, i) => (
                  <div key={i} className="border border-border/60 rounded-md p-3" data-testid={`import-factor-row-${i}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">{f.niveau}</span>
                        <p className="text-sm font-medium">{f.facteur}</p>
                      </div>
                      <Button type="button" size="icon" variant="ghost" data-testid={`import-remove-factor-${i}`} onClick={() => setFactors(factors.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                    <Textarea rows={1} placeholder="Justification" value={f.justification || ""} onChange={(e) => setFactors(factors.map((x, j) => (j === i ? { ...x, justification: e.target.value } : x)))} />
                  </div>
                ))}
                {factors.length === 0 && <p className="text-xs text-muted-foreground">Aucun facteur extrait.</p>}
              </div>
            </div>

            <div className="bg-card border border-border rounded-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading uppercase text-sm font-semibold text-muted-foreground">Plan HFIX extrait ({hfixActions.length})</h2>
                <Button type="button" size="sm" variant="outline" data-testid="import-add-hfix" onClick={() => setHfixActions([...hfixActions, { description_action: "", faisabilite: 3, acceptabilite: 3, couts_vs_avantages: 3, efficacite: 3, durabilite: 3 }])} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Ajouter
                </Button>
              </div>
              <div className="space-y-4" data-testid="import-hfix-list">
                {hfixActions.map((a, i) => (
                  <div key={i} className="border border-border/60 rounded-md p-3 space-y-2" data-testid={`import-hfix-row-${i}`}>
                    <div className="flex items-start gap-2">
                      <Textarea rows={2} className="flex-1" value={a.description_action} onChange={(e) => setHfixActions(hfixActions.map((x, j) => (j === i ? { ...x, description_action: e.target.value } : x)))} />
                      <Button type="button" size="icon" variant="ghost" data-testid={`import-remove-hfix-${i}`} onClick={() => setHfixActions(hfixActions.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                    <div className="flex gap-4 flex-wrap">
                      {HFIX_CRITERIA.map((c) => (
                        <div key={c.key} className="flex items-center gap-1.5">
                          <span className="text-[11px] text-muted-foreground">{c.label}</span>
                          <div className="flex gap-0.5" data-testid={`import-hfix-${i}-score-${c.key}`}>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <button type="button" key={n} onClick={() => setHfixActions(hfixActions.map((x, j) => (j === i ? { ...x, [c.key]: n } : x)))}
                                className={`h-5 w-5 text-[10px] rounded-sm border ${Number(a[c.key]) === n ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {hfixActions.length === 0 && <p className="text-xs text-muted-foreground">Aucune action HFIX extraite.</p>}
              </div>
            </div>

            <Button type="submit" disabled={submitting} data-testid="import-confirm-button" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer l'import"}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
